import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
  Avatar,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreHoriz as MoreHorizIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  ExitToApp as ExitIcon,
  Delete as DeleteIcon,
  FolderSpecial as ProjectIcon,
  SmartToy as SmartToyIcon,
  Tag as TagIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { getMyChatRooms, leaveChatRoom, deleteChatRoom } from '../../lib/api/chatApi';
import { useSelector } from 'react-redux';

// ─── 색상 팔레트 (Slack 스타일 다크 사이드바) ───────────────────────────────
const SIDEBAR = {
  bg: '#1a1d21',
  bgHover: '#27242c',
  bgActive: '#1164a3',
  text: '#c9cdd4',
  textMuted: '#7a7f87',
  textActive: '#ffffff',
  textHeader: '#e8eaf0',
  border: '#2d3138',
  badge: '#e01e5a',
  icon: '#8a8e98',
  searchBg: '#2d3138',
  searchBorder: '#484c55',
  divider: 'rgba(255,255,255,0.08)',
};

// ─── 채팅방 유형별 아이콘 & 색상 ─────────────────────────────────────────────
const getRoomMeta = (roomTyp, isSystem = false) => {
  if (isSystem) return { icon: <SmartToyIcon sx={{ fontSize: 16 }} />, color: '#1d9bd1' };
  switch (roomTyp) {
    case 'PROJECT': return { icon: <ProjectIcon sx={{ fontSize: 16 }} />, color: '#e8912d' };
    case 'TEAM':    return { icon: <GroupIcon    sx={{ fontSize: 16 }} />, color: '#2eb67d' };
    default:        return { icon: <TagIcon      sx={{ fontSize: 16 }} />, color: SIDEBAR.icon };
  }
};

// ─── 섹션 그룹 헤더 ────────────────────────────────────────────────────────────
const SectionHeader = ({ label, count, onAdd, expanded, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 1.5,
      py: 0.5,
      cursor: 'pointer',
      userSelect: 'none',
      '&:hover .add-btn': { opacity: 1 },
      '&:hover': { bgcolor: SIDEBAR.bgHover, borderRadius: 1 },
    }}
  >
    <Box sx={{ color: SIDEBAR.textMuted, mr: 0.5, display: 'flex', alignItems: 'center' }}>
      {expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
    </Box>
    <Typography
      variant="caption"
      sx={{
        color: SIDEBAR.textMuted,
        fontWeight: 700,
        fontSize: '0.7rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        flexGrow: 1,
      }}
    >
      {label}
      {count > 0 && (
        <span style={{ marginLeft: 6, opacity: 0.6 }}>({count})</span>
      )}
    </Typography>
    {onAdd && (
      <Tooltip title="새 채널 만들기" placement="right">
        <IconButton
          className="add-btn"
          size="small"
          onClick={(e) => { e.stopPropagation(); onAdd(); }}
          sx={{
            opacity: 0,
            transition: 'opacity 0.15s',
            color: SIDEBAR.textMuted,
            p: 0.25,
            '&:hover': { color: SIDEBAR.textActive, bgcolor: 'transparent' },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

// ─── 채널 아이템 ──────────────────────────────────────────────────────────────
const ChannelItem = ({ room, isSelected, onSelect, onMenuOpen }) => {
  const [hovered, setHovered] = useState(false);
  const meta = getRoomMeta(room.chatRoomTyp, room.isSystem);
  const hasUnread = room.unreadCount > 0;

  return (
    <ListItem
      disablePadding
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ mb: 0.25 }}
    >
      <ListItemButton
      onClick={() => onSelect(room.chatRoomUuid, room.name)}
        sx={{
          px: 1.5,
          py: 0.6,
          borderRadius: 1.5,
          mx: 0.5,
          bgcolor: isSelected ? SIDEBAR.bgActive : 'transparent',
          '&:hover': {
            bgcolor: isSelected ? SIDEBAR.bgActive : SIDEBAR.bgHover,
          },
          transition: 'background-color 0.1s',
        }}
      >
        {/* 채널 아이콘 */}
        <Box
          sx={{
            mr: 1.5,
            color: isSelected ? SIDEBAR.textActive : (hasUnread ? '#c9cdd4' : meta.color),
            display: 'flex',
            alignItems: 'center',
            opacity: isSelected ? 1 : 0.85,
          }}
        >
          {meta.icon}
        </Box>

        {/* 채널명 */}
        <ListItemText
          primary={
            <Typography
              noWrap
              sx={{
                fontSize: '0.875rem',
                fontWeight: hasUnread ? 700 : (isSelected ? 600 : 400),
                color: isSelected
                  ? SIDEBAR.textActive
                  : hasUnread
                  ? '#e8eaf0'
                  : SIDEBAR.text,
                lineHeight: 1.3,
              }}
            >
              {room.name}
            </Typography>
          }
          secondary={
            room.lastMessage && !isSelected ? (
              <Typography
                noWrap
                sx={{ fontSize: '0.72rem', color: SIDEBAR.textMuted, lineHeight: 1.2 }}
              >
                {room.lastMessage}
              </Typography>
            ) : null
          }
          sx={{ my: 0 }}
        />

        {/* 오른쪽 영역: 뱃지 or 메뉴 */}
        <Box sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hasUnread && !hovered && (
            <Box
              sx={{
                bgcolor: SIDEBAR.badge,
                color: '#fff',
                borderRadius: 6,
                minWidth: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.65rem',
                fontWeight: 700,
                px: 0.5,
              }}
            >
              {room.unreadCount > 99 ? '99+' : room.unreadCount}
            </Box>
          )}
          {!room.isSystem && hovered && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onMenuOpen(e, room); }}
              sx={{
                p: 0.25,
                color: isSelected ? SIDEBAR.textActive : SIDEBAR.textMuted,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <MoreHorizIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

// ─── 메인 컴포넌트 ────────────────────────────────────────────────────────────
const ChatChannelList = ({
  selectedRoomId,
  onSelectRoom,
  onCreateRoom,
  onOpenSettings,
  mobile = false,
  onSelectComplete,
}) => {
  const [chatRooms, setChatRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [sectionExpanded, setSectionExpanded] = useState({ system: true, channels: true });

  const { user } = useSelector(({ auth }) => ({ user: auth.user }));

  const systemChannel = {
    chatRoomUuid: user?.usrUuid,
    name: '내 채팅 (KMES BOT)',
    chatRoomTyp: 'SYSTEM',
    memberCount: 1,
    myRole: 'OWNER',
    isSystem: true,
    unreadCount: 0,
  };

  const fetchChatRooms = useCallback(async () => {
    try {
      setLoading(true);
      const rooms = await getMyChatRooms();
      setChatRooms(rooms);
    } catch (error) {
      console.error('채팅방 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChatRooms();
  }, [fetchChatRooms]);

  const filteredRooms = chatRooms.filter(
    (room) =>
      room.name?.toLowerCase().includes(searchKeyword.toLowerCase()) &&
      room.chatRoomUuid !== user?.usrUuid
  );

  const showSystemChannel =
    !searchKeyword ||
    systemChannel.name.toLowerCase().includes(searchKeyword.toLowerCase());

  const handleMenuOpen = (event, room) => {
    setAnchorEl(event.currentTarget);
    setSelectedRoom(room);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRoom(null);
  };

  const handleLeaveRoom = async () => {
    if (!selectedRoom) return;
    try {
      await leaveChatRoom(selectedRoom.chatRoomUuid);
      fetchChatRooms();
      if (selectedRoom.chatRoomUuid === selectedRoomId) onSelectRoom(null);
    } catch (error) {
      console.error('채팅방 나가기 실패:', error);
    }
    handleMenuClose();
  };

  const handleDeleteRoom = async () => {
    if (!selectedRoom) return;
    if (!window.confirm('정말로 이 채팅방을 삭제하시겠습니까?')) { handleMenuClose(); return; }
    try {
      await deleteChatRoom(selectedRoom.chatRoomUuid);
      fetchChatRooms();
      if (selectedRoom.chatRoomUuid === selectedRoomId) onSelectRoom(null);
    } catch (error) {
      alert('채팅방을 삭제할 권한이 없습니다.');
    }
    handleMenuClose();
  };

  const toggleSection = (key) =>
    setSectionExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSelectRoom = (roomUuid, roomName) => {
    onSelectRoom(roomUuid, roomName);
    onSelectComplete?.();
  };

  const handleCreateRoom = () => {
    onCreateRoom();
    onSelectComplete?.();
  };

  return (
    <Box
      sx={{
        width: mobile ? 'min(320px, 88vw)' : 260,
        minWidth: mobile ? 0 : 260,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: SIDEBAR.bg,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {/* ── 워크스페이스 헤더 ───────────────────────────── */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${SIDEBAR.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 52,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1rem',
            color: SIDEBAR.textHeader,
            letterSpacing: '-0.01em',
          }}
        >
          KSM 채팅
        </Typography>
        <Tooltip title="새 채널 만들기" placement="bottom">
          <IconButton
            size="small"
            onClick={handleCreateRoom}
            sx={{
              bgcolor: 'rgba(255,255,255,0.08)',
              color: SIDEBAR.text,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' },
              width: 28,
              height: 28,
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── 검색창 ─────────────────────────────────────── */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="채널 검색"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: SIDEBAR.textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: SIDEBAR.searchBg,
              borderRadius: 1.5,
              fontSize: '0.8rem',
              color: SIDEBAR.text,
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: SIDEBAR.searchBorder },
              '&.Mui-focused fieldset': { borderColor: SIDEBAR.bgActive },
              '& input::placeholder': { color: SIDEBAR.textMuted, opacity: 1 },
              '& input': { py: 0.75, px: 0.5 },
            },
          }}
        />
      </Box>

      {/* ── 채널 목록 ───────────────────────────────────── */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pb: 2,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={20} sx={{ color: SIDEBAR.textMuted }} />
          </Box>
        ) : (
          <>
            {/* 시스템 채널 섹션 */}
            {showSystemChannel && user?.usrUuid && (
              <Box sx={{ mt: 1.5 }}>
                <SectionHeader
                  label="직접 메시지"
                  count={0}
                  expanded={sectionExpanded.system}
                  onToggle={() => toggleSection('system')}
                />
                <Collapse in={sectionExpanded.system}>
                  <List sx={{ py: 0.25 }} disablePadding>
                    <ChannelItem
                      room={systemChannel}
                      isSelected={selectedRoomId === systemChannel.chatRoomUuid}
                      onSelect={handleSelectRoom}
                      onMenuOpen={handleMenuOpen}
                    />
                  </List>
                </Collapse>
              </Box>
            )}

            {/* 채널 섹션 */}
            <Box sx={{ mt: 2 }}>
              <SectionHeader
                label="채널"
                count={filteredRooms.length}
                onAdd={handleCreateRoom}
                expanded={sectionExpanded.channels}
                onToggle={() => toggleSection('channels')}
              />
              <Collapse in={sectionExpanded.channels}>
                <List sx={{ py: 0.25 }} disablePadding>
                  {filteredRooms.map((room) => (
                    <ChannelItem
                      key={room.chatRoomUuid}
                      room={room}
                      isSelected={selectedRoomId === room.chatRoomUuid}
                      onSelect={handleSelectRoom}
                      onMenuOpen={handleMenuOpen}
                    />
                  ))}
                  {filteredRooms.length === 0 && (
                    <Box sx={{ px: 2.5, py: 1.5 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: SIDEBAR.textMuted }}>
                        {searchKeyword ? '검색 결과 없음' : '참여 중인 채널이 없습니다'}
                      </Typography>
                    </Box>
                  )}
                </List>
              </Collapse>
            </Box>
          </>
        )}
      </Box>

      {/* ── 하단 유저 영역 ──────────────────────────────── */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderTop: `1px solid ${SIDEBAR.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: '#1164a3',
              fontSize: '0.75rem',
              fontWeight: 700,
            }}
          >
            {user?.usrNm?.charAt(0) || '?'}
          </Avatar>
          <CircleIcon
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              fontSize: 10,
              color: '#2eb67d',
              bgcolor: SIDEBAR.bg,
              borderRadius: '50%',
            }}
          />
        </Box>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: SIDEBAR.text, flexGrow: 1 }} noWrap>
          {user?.usrNm || '사용자'}
        </Typography>
      </Box>

      {/* ── 컨텍스트 메뉴 ──────────────────────────────── */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: '#1e2129',
            border: `1px solid ${SIDEBAR.border}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            minWidth: 180,
            '& .MuiMenuItem-root': {
              fontSize: '0.85rem',
              color: '#c9cdd4',
              py: 1,
              '&:hover': { bgcolor: SIDEBAR.bgActive, color: '#fff' },
            },
          },
        }}
      >
        <MenuItem onClick={() => { onOpenSettings(selectedRoom); handleMenuClose(); }}>
          <SettingsIcon sx={{ fontSize: 16, mr: 1.5 }} />
          채널 설정 / 멤버 관리
        </MenuItem>
        <Divider sx={{ borderColor: SIDEBAR.divider, my: 0.5 }} />
        <MenuItem onClick={handleLeaveRoom}>
          <ExitIcon sx={{ fontSize: 16, mr: 1.5 }} />
          채널 나가기
        </MenuItem>
        {selectedRoom?.myRole === 'OWNER' && (
          <MenuItem
            onClick={handleDeleteRoom}
            sx={{ color: '#e01e5a !important', '&:hover': { bgcolor: 'rgba(224,30,90,0.15) !important', color: '#ff4d6d !important' } }}
          >
            <DeleteIcon sx={{ fontSize: 16, mr: 1.5 }} />
            채널 삭제
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ChatChannelList;
